<!-- SubscriptionExpiredModal.vue
     Modal informativo exibido quando a assinatura paga do usuário
     expira (fim de período pago, com ou sem pausa prévia).
     ─────────────────────────────────────────────────────────────
     Refatorado para seguir o mesmo padrão visual de
     CancelImmediateConfirmModal.vue:
       - painel branco centrado, alinhado pelo topo
       - ícone redondo no topo (variante âmbar — alerta informativo,
         não destrutivo como o vermelho do cancelamento)
       - título / corpo / ações em coluna, sem header/footer separados
       - animação fade + scale via <transition>

     Difere do CancelImmediate em dois pontos por ser informativo
     (e não confirmacional):
       - uma única ação ("Fechar"), centralizada
       - cor do ícone âmbar (warn) em vez de vermelho (danger)
-->

<script setup>
import { onMounted, onBeforeUnmount, watch } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },
  // Mensagem textual completa (ex.: "Sua assinatura Plus expirou e
  // você foi movido para o plano gratuito.").
  message: { type: String, required: true },
});

const emit = defineEmits(["close"]);

// 🔹 NOVO: arrastar o painel pelo título (não há cabeçalho próprio aqui).
// O componente não desmonta entre aberturas, então o watch recentraliza.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

function close() {
  emit("close");
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape") close();
}

onMounted(() => document.addEventListener("keydown", handleEscKey));
onBeforeUnmount(() => document.removeEventListener("keydown", handleEscKey));
</script>

<template>
  <Teleport to="body">
    <transition name="modal-fade" appear>
      <div
        v-if="show"
        class="modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-expired-title"
        @click.self="close"
      >
        <div ref="panelRef" class="modal-panel modal-panel--compact" :style="panelStyle">
          <div class="modal-panel__icon">
            <!-- triângulo de alerta — mesmo ícone do componente original,
                 apenas reposicionado dentro da bolha redonda no padrão. -->
            <svg
              class="modal-panel__icon-svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <h4
            id="subscription-expired-title"
            class="modal-panel__title"
            :class="{
 'modal-panel__title--draggable': isDraggable,
              'modal-panel__title--dragging': isDragging,
            }"
            @pointerdown="onHandlePointerDown"
          >
            Aviso de Assinatura
          </h4>

          <p class="modal-panel__body">{{ message }}</p>

          <div class="modal-panel__actions">
            <button type="button" class="btn btn-secondary" @click="close">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* Bolha redonda do ícone — paleta âmbar (alerta informativo),
   coerente com o badge "Pausada" do AccountSummary e com o
   summary-block--warn usado anteriormente neste mesmo componente. */
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--warning-bg);
  border: 1px solid var(--warning-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b45309;
  margin-bottom: 0.25rem;
}

.modal-panel__icon-svg {
  width: 1.5rem;
  height: 1.5rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

/* 🔹 NOVO: título como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.modal-panel__title--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.modal-panel__title--dragging {
  cursor: grabbing;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}
</style>